import { useState, useCallback, useEffect, useRef } from 'react';
import { Flex, Radio, Button } from 'antd';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import { motion } from 'framer-motion';
import BaseRichTextEditor from 'components/atoms/base-rich-text-editor';
import BaseDatepicker from 'components/atoms/base-datepicker';
import BaseSwitch from 'components/atoms/base-switch';
import { ModuleType, DateFormats } from 'infrastructure/enums';
import { isHtmlEmpty } from 'utils';
import { addDays } from 'date-fns';

import styles from './styles.module.scss';

import type { RadioChangeEvent } from 'antd';

const CHARACTER_LIMIT = 1000;

const getPlainTextLength = (htmlString: string): number => {
  if (!htmlString) return 0;
  return htmlString.replace(/<[^>]*>/g, '').trim().length;
};

interface INoteComposerProps {
  initialModule: ModuleType;
  onSave: (values: {
    note: string;
    expiresAt?: Date | null;
    module: ModuleType;
  }) => void;
  onCancel: () => void;
  onModuleChange?: (module: ModuleType) => void;
  isLoading?: boolean;
  isEditMode?: boolean;
  initialValues?: {
    note: string;
    expiresAt?: Date | null;
  };
  availableModules?: ModuleType[];
}

export const NoteComposer = ({
  initialModule,
  onSave,
  onCancel,
  onModuleChange,
  isLoading,
  isEditMode = false,
  initialValues,
  availableModules,
}: INoteComposerProps) => {
  const { t } = useTranslation();
  const [hasExpiration, setHasExpiration] = useState(
    !!initialValues?.expiresAt,
  );
  const wasLoadingRef = useRef(false);

  const formik = useFormik({
    initialValues: {
      note: initialValues?.note || '',
      expiresAt: initialValues?.expiresAt || null,
      module: initialModule,
    },
    enableReinitialize: false,
    onSubmit: (values) => {
      onSave(values);
    },
  });

  useEffect(() => {
    formik.setFieldValue('module', initialModule);
  }, [initialModule]);

  useEffect(() => {
    const modulePool =
      availableModules && availableModules?.length > 0
        ? availableModules
        : [ModuleType.RPM, ModuleType.CCM];

    if (!modulePool.includes(formik.values.module)) {
      const fallbackModule = modulePool[0] ?? ModuleType.RPM;
      formik.setFieldValue('module', fallbackModule);
      onModuleChange?.(fallbackModule);
    }
  }, [availableModules, formik.values.module, onModuleChange]);

  useEffect(() => {
    if (initialValues?.expiresAt) {
      setHasExpiration(true);
    }
  }, [initialValues?.expiresAt]);

  useEffect(() => {
    if (wasLoadingRef.current && !isLoading) {
      formik.resetForm();
      setHasExpiration(false);
    }
    wasLoadingRef.current = isLoading || false;
  }, [isLoading]);

  const handleNoteChange = useCallback(
    (value: string) => {
      if (getPlainTextLength(value) <= CHARACTER_LIMIT) {
        formik.setFieldValue('note', value);
      }
    },
    [formik.setFieldValue],
  );

  const handleCancel = useCallback(() => {
    formik.resetForm();
    setHasExpiration(false);
    onCancel();
  }, [onCancel]);

  const handleModuleChange = useCallback(
    (e: RadioChangeEvent) => {
      const newModule = e.target.value;
      if (!availableModules?.includes(newModule)) return;
      formik.setFieldValue('module', newModule);
      onModuleChange?.(newModule);
    },
    [availableModules, onModuleChange],
  );

  const handleExpirationToggle = useCallback((checked: boolean) => {
    setHasExpiration(checked);
    if (!checked) {
      formik.setFieldValue('expiresAt', null);
    }
  }, []);

  const minDate = addDays(new Date(), 1);
  const moduleOptions = (
    availableModules?.length
      ? availableModules
      : [ModuleType.RPM, ModuleType.CCM]
  ).map((module) => ({
    label:
      module === ModuleType.RPM ? t('tabs.rpm', 'RPM') : t('tabs.ccm', 'CCM'),
    value: module,
  }));
  const showModuleSelector = !isEditMode && moduleOptions.length > 1;

  return (
    <motion.div
      initial={{ opacity: 0, height: 0, marginBottom: 0 }}
      animate={{ opacity: 1, height: 'auto', marginBottom: 25 }}
      exit={{ opacity: 0, height: 0, marginBottom: 0 }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      className={styles['note-composer-wrapper']}
    >
      <div className={styles['note-composer']}>
        <Flex vertical gap={16}>
          <Flex justify="space-between" align="center">
            <h3 className={styles['note-composer-title']}>
              {isEditMode
                ? t('labels.editKeynote', 'Edit Key Note')
                : t('labels.addNewKeynote', 'Add New Key Note')}
            </h3>
            {showModuleSelector && (
              <Radio.Group
                value={formik.values.module}
                onChange={handleModuleChange}
                className={styles['note-composer-radio']}
                disabled={isLoading}
              >
                {moduleOptions.map((option) => (
                  <Radio key={option.value} value={option.value}>
                    {option.label}
                  </Radio>
                ))}
              </Radio.Group>
            )}
          </Flex>

          <div className={styles['note-composer-editor']}>
            <BaseRichTextEditor
              value={formik.values.note}
              setValue={handleNoteChange}
              disabled={!!isLoading}
            />
          </div>

          <Flex vertical gap={16}>
            <Flex gap={12} align="center">
              <BaseSwitch
                value={hasExpiration}
                onChange={handleExpirationToggle}
                label={t('labels.expirationDate', 'Set expiration date')}
                disabled={isLoading}
              />
            </Flex>
            <BaseDatepicker
              minDate={minDate}
              onChange={(date) => formik.setFieldValue('expiresAt', date)}
              disabled={!hasExpiration || isLoading}
              value={formik.values.expiresAt || undefined}
              className={styles['note-composer-datepicker']}
              format={DateFormats['MM/DD/YY']}
              placeholder="MM/DD/YY"
            />
          </Flex>
        </Flex>

        <Flex
          gap={8}
          justify="flex-end"
          className={styles['note-composer-actions']}
        >
          <Button
            disabled={isLoading}
            onClick={handleCancel}
            className={styles['cancel-button']}
          >
            {t('labels.cancel', 'Cancel')}
          </Button>
          <Button
            type="primary"
            loading={isLoading}
            disabled={isHtmlEmpty(formik.values.note)}
            onClick={() => formik.handleSubmit()}
          >
            {isEditMode
              ? t('labels.saveNote', 'Save Note')
              : t('labels.addKeynote', 'Add Note')}
          </Button>
        </Flex>
      </div>
    </motion.div>
  );
};
